import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Identifier, Group, ImportedTagDefinition, IdentifierType, SelectionStrategy, ObjectType } from '../../../models';
import { IGroupsImportService, IGroupService, ItagsImportService, ItagsMappingService, IStoreService, ITranslatedLoggerService } from '../../../services';
import { StoreElement } from '../../../stateManagement';
import { IRootScope } from '../../../IRootScope';
import { toggleEditMode, groupTreeToggled, identifierSelected, formDirtyChanged, groupTreeChanged, groupSaved, identifierSaved } from '../../../common/events';
import { guidEmpty, Helper } from '../../../common';

class IdentifiersMappingStateController implements angular.IComponentController {
    createValidated = false;
    displayed: ImportedTagDefinition[];
    dragObject: any;
    dropGroup: Group;
    foundGroups: Group[];
    identifiersMenu: any[];
    importAliasCallback: any;
    isEditMode: boolean;
    isGroupTreeHidden: boolean;
    isLoading: boolean;
    isSelectionPrevented: boolean;
    mappingResult: any;
    proposedAlias: any[];
    searchMode = 0;
    selectedConnectionPoint: string;
    selectedIdentifier: Identifier;
    selectedGroup: Group;
    unsavedList: any[];

    private _groups: Group[];
    private _garbarges: (() => void)[] = [];

    static $inject = [
        '$q',
        '$scope',
        '$stateParams',
        '$rootScope',
        '$translate',
        'storeService',
        'tagsMappingService',
        'tagsImportService',
        'groupService',
        'GroupsImportService',
        'translatedLoggerService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$stateParams: angular.ui.IStateParamsService,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _tagsMappingService: ItagsMappingService,
        private _tagsImportService: ItagsImportService,
        private _groupService: IGroupService,
        private _grouspImportService: IGroupsImportService,
        private _loggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this.isEditMode = this._$rootScope.isEditMode;

        this._$scope.$on(toggleEditMode, (event, data) => {
            this.isEditMode = this._$rootScope.isEditMode;
        });

        this._$scope.$watch(() => this.identifiersMenu, (newValue) => {
            //TODO crappy, use property names instead
            if (newValue) {
                newValue[2].isVisible = false;
                newValue[6].isVisible = false;
                newValue[7].isVisible = false;
                newValue[9].isVisible = false;
            }
        });

        this._$scope.$on(groupTreeToggled, () => {
            this.isGroupTreeHidden = !this.isGroupTreeHidden;
        });

        if (this._$stateParams['connectionPointGuid']) {
            this.selectedConnectionPoint = this._$stateParams['connectionPointGuid'];
        }

        this.searchMode = this._$stateParams['searchMode'] ? parseInt(this._$stateParams['searchMode']) : 0;

        this._$scope.$on(formDirtyChanged, (event, isDirty) => {
            this.isSelectionPrevented = isDirty;
        });

        //not a component, so $onDestroy is not called
        this._$scope.$on('$destroy', () => {
            this._$rootScope.$broadcast(identifierSelected, null);
            this.$onDestroy();
        });

        this._garbarges.push(this._storeService.register(
            (store) => {
                this._groups = _.values(store.groups.dictionary);
            },
            [StoreElement.Groups]
        ));
    }

    $onDestroy() {
        this._garbarges.forEach(w => w());
    }

    private loadGroups(force = false): angular.IPromise<Group[]> {
        return this._$q.all([
            this._storeService.dispatchers().valueViewModels.getAll(force),
            this._storeService.dispatchers().groups.getAll(force)
        ])
            .then(data => {
                this.tryReselect(data[1]);
                return data[1];
            });
    }

    private tryReselect(groups: Group[]) {
        if (this.selectedGroup) {
            this.selectedGroup = _.find(groups, g => g.guid === this.selectedGroup.guid);
            if (this.selectedGroup && this.selectedIdentifier) {
                this.selectedIdentifier = _.find(this.selectedGroup.identifierCollection,
                    i => i.guid === this.selectedIdentifier.guid);
            }
        }
    }

    drop(dummy, dragObject: ImportedTagDefinition) {
        this.addIdentifier(this.selectedGroup, dragObject);
    }

    mapTagToIdentifier(identifier: Identifier, dragObject: any): void {
        if (identifier.guid !== dragObject.guid) {
            if (identifier.type === IdentifierType.Validated) {
                identifier.selectionStrategy = SelectionStrategy.Tags;
            }
            this._tagsImportService
                .searchUnknownUnits([dragObject].filter(t => !t.isAlreadyExisting))
                .then(data => {
                    if (data && data.length > 0) {
                        this.proposedAlias = data.map(d => {
                            return { name: d, save: true };
                        });
                        this.importAliasCallback = (extraAliases) => {
                            this.actualMap(dragObject, identifier, extraAliases);
                        };
                        $('#aliasModal').modal('show');
                    } else {
                        this.actualMap(dragObject, identifier, []);
                    }
                });
        }
        else {
            this._loggerService.logError('You can\'t drag an object on himself!');
        }
    }

    dropOnGroup(group: Group, dragObject: any): void {
        if (dragObject) {
            if (dragObject.objectType === 'Group') {
                const same = this._groups.filter(g => g.importedId === dragObject.importedId);
                if (same.length && group.importedId !== dragObject.importedId) {
                    if (!group.uniqueName) {
                        group.uniqueName = 'root';
                    }
                    this.chooseRootModal(same, group, dragObject);
                } else {
                    this.preImportGroup(group, dragObject);
                }
            } else if (dragObject.objectType === ObjectType.Identifier) {
                this.duplicateIdentifier(group, dragObject);
            } else {
                this.addIdentifier(group, dragObject);
            }
        }
    }

    private duplicateIdentifier(group: Group, dragObject: any) {
        this.loadGroups()
            .then(groups => {
                const foundGroup = _.find(groups, g => g.guid === group.guid);
                if (_.some(foundGroup.identifierCollection, i => i.name === dragObject.name)) {
                    const message = 'Identifier _identifierName_ already exists in group _groupName_';
                    this._loggerService.logWarning(this._$translate.instant(message,
                        { groupName: foundGroup.name, identifierName: dragObject.name }), true);
                } else {
                    this.isLoading = true;
                    const identifiers = _.chain(groups)
                        .map(g => g.identifierCollection)
                        .flatten()
                        .value();
                    const found = _.find(identifiers, i => i.guid === dragObject.guid);
                    const copy = angular.copy(found);
                    copy.guid = Helper.getGuid();
                    copy.templateGuid = guidEmpty;
                    this._groupService
                        .addIdentifier(group.guid, copy)
                        .then(data => {
                            this._$rootScope.$broadcast(groupTreeChanged, {});
                            this.loadGroups(true);
                            this._storeService.dispatchers().valueViewModels.getAll(true);
                        })
                        .finally(() => this.isLoading = false);
                }

            });
    }

    private chooseRootModal(same: Group[], group: Group, dragObject: any) {
        this.foundGroups = _.sortBy(same, d => d.uniqueName);
        this.dragObject = dragObject;
        this.dropGroup = group;
        const modalName = 'mapGroupModal';
        $(`#${modalName}`).modal('show');
    }

    preImportGroup(group: Group, dragObject: any) {
        if (group && dragObject) {
            this._grouspImportService
                .checkNumberOftags(dragObject)
                .then(data => {
                    if (!data || confirm(
                        this._$translate.instant(
                            'Importing this group will exceed the number of authorized tags. Do you want to continue?'))
                    ) {
                        return this._grouspImportService.findUnkown(dragObject);
                    }
                })
                .then(data => {
                    if (data && data.length > 0) {
                        this.proposedAlias = data.map(d => {
                            return { name: d, save: true };
                        });

                        this.importAliasCallback = (extraAliases) => {
                            this.importGroup(dragObject, group, extraAliases);
                        };
                        $('#aliasModal').modal('show');
                    } else {
                        this.importGroup(dragObject, group, []);
                    }
                });
        }
    }

    private importGroup(dragObject, group, extraAliases) {
        this.isLoading = true;
        this._grouspImportService
            .import(dragObject, group.guid, this.selectedConnectionPoint, extraAliases)
            .then(groups => {
                this._loggerService.logSuccess(`${groups.length} groups created.`);
                return this.loadGroups(true);
            })
            .then(() => {
                this._$rootScope.$broadcast(groupTreeChanged);
            })
            .catch(() => this._loggerService.logError(`Failed to create groups.`))
            .finally(() => this.isLoading = false);
    }

    addIdentifier(group: Group, dragObject: ImportedTagDefinition): void {
        this.isLoading = true;
        try {
            const tagImportList = [dragObject].filter(t => !t.isAlreadyExisting);
            this._tagsImportService
                .searchUnknownUnits(tagImportList)
                .then(data => {
                    if (data && data.length > 0) {
                        this.proposedAlias = data.map(d => {
                            return { name: d, save: true };
                        });

                        this.importAliasCallback = (extraAliases) => {
                            this.actualAdd([dragObject], group, extraAliases);
                        };
                        $('#aliasModal').modal('show');
                    } else {
                        this.actualAdd([dragObject], group, []);
                    }
                })
                .finally(() => this.isLoading = false);
        } catch (ex) {
            this._loggerService.logError(ex.message, true);
            this.isLoading = false;
        }
    }

    updateIdentifier(identifier: Identifier): void {
        this.isLoading = true;
        this._groupService
            .updateIdentifiers(this.selectedGroup.guid, [identifier])
            .then(data => {
                this._loggerService.logSuccess('Successfully updated identifier', true);
                this.selectedGroup.identifierCollection = data;
                this._$rootScope.$broadcast(identifierSaved, identifier);
                this._storeService.dispatchers().valueViewModels.getAll(true);
            })
            .catch(() => this._loggerService.logError('Failed to update identifier', true))
            .finally(() => this.isLoading = false);
    }

    importAll() {
        if (this.displayed && this.displayed.length > 0) {
            this.isLoading = true;
            this._tagsImportService
                .searchUnknownUnits(this.displayed.filter(t => !t.isAlreadyExisting))
                .then(data => {
                    if (data && data.length > 0) {
                        this.proposedAlias = data.map(d => {
                            return { name: d, save: true };
                        });

                        this.importAliasCallback = (extraAliases) => {
                            this.actualAdd(this.displayed, this.selectedGroup, extraAliases);
                        };
                        $('#aliasModal').modal('show');
                    } else {
                        this.actualAdd(this.displayed, this.selectedGroup, []);
                    }
                })
                .finally(() => this.isLoading = false);
        }
    }

    private actualMap(dragObject, identifiers, extraAliases) {
        this.isLoading = true;
        this._tagsMappingService
            .mapTagToIdentifier(dragObject, identifiers, extraAliases)
            .then(data => {
                if (data.isTagOk && data.isVariableTypeOk) {
                    this.updateIdentifier(data.identifier);
                } else {
                    this.mappingResult = data;
                    $('#mappingConflictModal').modal('show');
                }
            })
            .finally(() => this.isLoading = false);
    }

    private actualAdd(dragObject: ImportedTagDefinition[], group, extraAliases) {
        this.isLoading = true;
        const typeToCreate = this.createValidated ? IdentifierType.Validated : IdentifierType.Tag;
        this._tagsMappingService
            .addTagsToGroup(dragObject, group, extraAliases, typeToCreate)
            .then(d => {
                if (d) {
                    this._$rootScope.$broadcast(groupSaved, { savedGroup: d, tag: dragObject[0] });
                }
                this._$rootScope.$broadcast(groupTreeChanged);

                return this._$q.all([
                    this.loadGroups(true),
                    this._storeService.dispatchers().digitalSets.getAll(true),
                    // already loaded when reloading groups
                    //this.refreshValueViewModels(true);
                    this._storeService.dispatchers().tags.getAll(true)
                ]);
            }).finally(() => this.isLoading = false);
    }
}


export const wtIdentifiersMappingState: ComponentDefinition = {
    componentId: 'wtIdentifiersMappingState',
    controller: IdentifiersMappingStateController,
    template: require('./identifiersMappingState.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};